import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { ChartDataset, TooltipCallbacks } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import AirDatepicker from 'air-datepicker';
import { AnalyseService } from 'src/app/services/analyse/analyse.service';
import {
  AnalyzeConsumptionPrice,
  AnalyzeData
} from 'src/app/services/analyse/analyse.types';
import { ComponentsModule } from 'src/app/shared/components.module';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from 'src/app/tabs/forbruk/graphs/forbruk/charts/forbruk-charts-utils';
import {
  CustomLocaleNb,
  formatYYYYMMDDHHNumberToDate,
  formatYYYYMMDDNumberToDate,
  theTime
} from 'src/app/utils/date-utils';
import { subDays } from 'date-fns';
import { toCommaNumber } from 'src/app/tabs/forbruk/graphs/forbruk/forbrukstall/forbrukstall-utils';

@Component({
  selector: 'app-usage-cost-day',
  standalone: true,
  imports: [CommonModule, IonicModule, ComponentsModule],
  styleUrls: ['./usage-cost.component.scss'],
  template: `
    <ion-row>
      <ion-col>
        <h3>Bruker du mye strøm i dyre timer?</h3>
        <p class="border border-[--primary-strong] rounded-lg p-4">
          <strong>
            @if (priceHours.result === 'WHOHOO_YOU_ARE_DOING_GREAT') { Du gjør
            det kjempebra, fortsett sånn! } @if (priceHours.result ===
            'KEEP_CALM_AND_CARRY_ON_AS_USUAL') { Du gjør det bra, fortsett som
            du gjør. } @if (priceHours.result === 'REDUCTION_POSSIBLE') { Du har
            potensiale til å spare penger ved å flytte forbruket ditt til mer
            gunstige timer på dagen gjennom måneden. }
          </strong>
          <span class="block">
            @if (targetRealDivergence > 0) { Du har spart
            <span class="bold text-[--primary-strong]"
              >{{ toCommaNumber(targetRealDivergence) }} kr</span
            >
            ved å flytte forbruk mellom timer denne måneden, bedre enn vi
            beregnet! } @if (targetRealDivergence < 0) { Vi tror du kunne spart
            <span class="bold text-[--primary-strong]"
              >{{ toCommaNumber(-targetRealDivergence) }} kr</span
            >
            gjennom måneden ved å flytte forbruk til mer gunstige timer gjennom
            dagene. } @if (targetRealDivergence === 0) { Du kan foreløpig ikke
            spare noe på å flytte forbruket ditt mellom timer. } @if
            (priceHours.result === 'REDUCTION_POSSIBLE') {
            <span class="mt-2 block text-sm italic">
              Det er ikke alt forbruk som kan flyttes, men hvis du har mulighet
              til å redusere forbruket på dyre timer fremover vil du kunne spare
              penger. Det kan være ting som å vente med å lade elbilen til etter
              middag, eller dusje på kvelden i stedet for på morgenen.
            </span>
            }
          </span>
        </p>

        <!-- Datepicker -->
        <ion-row class="my-8 text-center">
          <ion-col>
            @if (!isLoading) {
            <div class="">
              <span (click)="showHideCal()" class="day-selector">
                <ion-icon name="calendar"></ion-icon>&nbsp;{{
                  this.selectedDate
                    | date : 'd. MMMM yyyy' : '' : 'nb'
                    | titlecase
                }}
              </span>
            </div>
            }
            <div
              id="airUsageCostDay"
              class="datepicker-popup"
              [style.display]="showCal ? 'inline' : 'none'"
            ></div>
          </ion-col>
        </ion-row>

        <div class="mt-3">
          @if (analyzeData) {
          <app-line-chart
            chartId="usagecost-chart-day"
            [dataSets]="dataSets"
            [labels]="labels"
            [title]="title"
            [tooltipCallbacks]="tooltipCallbacks"
            [xUnit]="'hour'"
            [yUnit]="'kr'"
            [showYTitle]="true"
            [removeBackgroundColor]="true"
          ></app-line-chart>
          } @if (hasNoData) {
          <div class="rounded-lg bg-[--warning-40] p-4">
            <p>Beklager, her var det visst ikke noe data...</p>
          </div>
          }
        </div>

        <p class="border border-[--primary-strong] rounded-lg p-4">
          <ion-icon name="information-circle-outline"></ion-icon>
          <i
            class="ml-2 inline-block w-4 h-4 border-2 border-[--skog] bg-green-500/[.2]"
          ></i>
          Grønne felter i grafen viser billige perioder der du brukte lite
          strøm, mens
          <i
            class="inline-block w-4 h-4 border-2 border-[--tundra] bg-[--warning-20]"
          ></i>
          oransje felter viser dyre perioder der du brukte mye strøm.
        </p>
      </ion-col>
    </ion-row>
  `
})
export class UsageCostDayComponent {
  @Input() objectid: string;
  @Input() priceHours: AnalyzeConsumptionPrice;
  analyzeData: AnalyzeData;
  isLoading = false;
  hasNoData = false;

  period: string;
  dayDatepicker: AirDatepicker<HTMLElement>;
  selectedDate: Date;
  latestDate: Date;
  earliestDate: Date;
  showCal = false;

  targetRealDivergence: number;
  sumTargetRealDivergence: number;
  toCommaNumber = toCommaNumber;

  dataSets: ChartDataset<'line'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'line'>>;

  constructor(private analyseService: AnalyseService) { }

  ngOnInit() {
    this.isLoading = true;
    this.targetRealDivergence =
      this.priceHours.target_cost - this.priceHours.real_cost;

    const now = new Date();
    let dateToFetch: Date;
    if (now.getHours() < 12) {
      dateToFetch = subDays(now, 2);
    } else {
      dateToFetch = subDays(now, 1);
    }
    this.period = theTime(dateToFetch).to(['YYYY', 'MM', 'DD']);
    this.latestDate = dateToFetch;
    this.earliestDate = new Date(
      this.latestDate.getFullYear(),
      this.latestDate.getMonth(),
      1
    );
    this.selectedDate = this.latestDate;
    this.fetchData();
  }

  ngAfterViewInit() {
    this.dayDatepicker = new AirDatepicker<HTMLElement>('#airUsageCostDay', {
      startDate: this.selectedDate,
      locale: CustomLocaleNb,
      maxDate: this.latestDate,
      minDate: this.earliestDate,
      range: false,
      toggleSelected: false,
      view: 'days',
      dateFormat: 'd. MMMM yyyy',
      selectedDates: [this.selectedDate],
      onSelect: this.selectNewDate.bind(this)
    });
  }

  selectNewDate({
    date,
    formattedDate,
    datepicker
  }: {
    date: Date;
    formattedDate: string;
    datepicker: AirDatepicker<HTMLElement>;
  }) {
    if (date && formattedDate) {
      this.selectedDate = date;
      this.selectedDate.setHours(12);
      this.period = theTime(this.selectedDate, { DD: date.getDate() }).to(["YYYY", "MM", "DD"]);
      this.fetchData();
      this.showCal = false;
    }
  }

  fetchData() {
    this.isLoading = true;
    this.analyseService.getAnalyzeData(this.objectid, this.period).subscribe({
      next: (res) => {
        this.analyzeData = res;
        this.getChartData();
        this.isLoading = false;
        if (!res) {
          this.hasNoData = true;
        } else {
          this.hasNoData = false;
        }
      },
      error: (err) => {
        console.log(err);
        this.isLoading = false;
      }
    });
  }

  getChartData() {
    if (this.analyzeData) {
      this.title =
        'Kostnader ' +
        formatYYYYMMDDNumberToDate(Number(this.period)).toLocaleDateString(
          'nb-NO',
          {
            day: '2-digit',
            month: 'long'
          }
        );
      this.dataSets = [];
      this.tooltipCallbacks = {
        label: (tt) => labelTooltipCallback(tt, 'kr'),
        title: (tt) => titleTooltipCallback(tt, 'hour', 'kr'),
        footer: (tt) =>
          `Du brukte: ${Math.abs(Number(tt[0].raw) - Number(tt[1].raw)).toFixed(
            2
          )} kr ${Number(tt[0].raw) - Number(tt[1].raw) > 0 ? 'for lite' : 'for mye'
          }`
      };
      this.labels = this.analyzeData.items.map(
        (x) => formatYYYYMMDDHHNumberToDate(x.period - 1) // Hours are formatted as 01-24 initially
      );

      this.sumTargetRealDivergence = this.analyzeData.items
        .map((x) => x.real_cost - x.target_cost)
        .reduce((a, b) => a + b, 0);

      const dataReal = this.analyzeData.items.map((x) => x.real_cost || 0);
      const dataTarget = this.analyzeData.items.map((x) => x.target_cost || 0);

      this.dataSets.push({
        label: 'Optimal kostnad',
        data: dataTarget,
        pointStyle: 'line',
        fill: {
          above: 'rgba(38, 180, 27, 0.2)',
          below: 'rgba(255, 108, 0, 0.2)',
          target: '1'
        },
        backgroundColor: '#26b41b',
        borderColor: '#26b41b',
        borderWidth: 2,
        pointRadius: 1
      });

      this.dataSets.push({
        label: 'Faktisk kostnad',
        data: dataReal,
        pointStyle: 'line',
        backgroundColor: '#002987',
        borderColor: '#002987',
        borderWidth: 2,
        pointRadius: 1
      });
    }
  }

  showHideCal() {
    this.showCal = !this.showCal;
  }
}
